import { useCallback, useRef } from 'react';

/**
 * useTiltGlare — the pointer-tracked glare AND 3D tilt used by `HoloRoleCard`
 * (see its `.hrc-glare` — a radial highlight following the pointer, blended
 * with `mix-blend-mode: overlay`, fading with distance from the pointer — and
 * its `--rx`/`--ry` rotation driven by the same pointer position, clamped by
 * `MAX_TILT`). Both halves come from ONE pointer-move computation, so this is
 * the single shared handler for both effects — extracted here so OTHER cards
 * can reuse the identical recipe without forking it. HoloRoleCard itself is
 * frozen (do not touch) and keeps its own copy inline; this is the shareable
 * version for every new card that wants the same glare + tilt.
 *
 * Usage: wrap the tilting element in a `className="rm-tilt-scene"` parent
 * (provides `perspective`), spread the returned handlers + ref onto the
 * tilting element itself (`className="rm-tilt"`, e.g. the art/frame box), and
 * render a `className="rm-glare"` child inside it. Styles via `GLARE_CSS`. All
 * three custom properties (`--rx`/`--ry` for tilt, `--mx`/`--my`/`--glare` for
 * the sheen) are written directly to the ref'd node — no React re-render per
 * pointer-move.
 */

const MAX_TILT = 16; // deg — same clamp as HoloRoleCard

export function useTiltGlare<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width; // 0..1
    const py = (e.clientY - r.top) / r.height; // 0..1
    const rotY = (px - 0.5) * 2 * MAX_TILT; // pointer right → tilt right
    const rotX = -(py - 0.5) * 2 * MAX_TILT; // pointer down → tilt away
    const fromCenter = Math.min(1, Math.hypot(px - 0.5, py - 0.5) * 2);
    el.style.setProperty('--rx', `${rotX.toFixed(2)}deg`);
    el.style.setProperty('--ry', `${rotY.toFixed(2)}deg`);
    el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
    el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
    el.style.setProperty('--glare', `${(0.35 + fromCenter * 0.5).toFixed(2)}`);
  }, []);

  const onPointerLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
    el.style.setProperty('--mx', '50%');
    el.style.setProperty('--my', '50%');
    el.style.setProperty('--glare', '0');
  }, []);

  return { ref, onPointerMove, onPointerLeave };
}

/** Shared CSS — identical recipe to HoloRoleCard's `.hrc-scene`/`.hrc-card`/`.hrc-glare`. */
export const GLARE_CSS = `
.rm-tilt-scene { perspective: 1400px; }
.rm-tilt {
  --rx: 0deg; --ry: 0deg; --mx: 50%; --my: 50%; --glare: 0;
  transform-style: preserve-3d;
  transform: rotateX(var(--rx)) rotateY(var(--ry));
  transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  will-change: transform;
}
.rm-glare {
  position: absolute; inset: 0;
  border-radius: inherit;
  background: radial-gradient(
    farthest-corner circle at var(--mx) var(--my),
    rgba(255,255,255,0.55) 5%,
    rgba(255,255,255,0.18) 22%,
    rgba(0,0,0,0.35) 90%
  );
  mix-blend-mode: overlay;
  opacity: var(--glare);
  transition: opacity 0.4s ease;
  pointer-events: none;
  z-index: 4;
}
@media (prefers-reduced-motion: reduce) {
  .rm-tilt { transform: none !important; transition: none; }
  .rm-glare { transition: none; opacity: 0 !important; }
}
`;
